import React from 'react';
import { useAddMembersToTeam, useRemoveMemberFromTeam } from 'shared/hooks/rotations.hooks';
import { useRotationsContext } from '../../../rotations-context';
import { SectionComponent } from 'src/components/common/section/section.component';
import { CONTACT_TYPES, TEAM_GROUPS } from 'shared/constants';
import { formatFullName } from 'shared/utils';
import { TeamManager } from 'src/components/team/team-manager.component';
import styles from '../../rotation-content.module.scss';

const TeamTabComponent = ({ contacts, getContactById }) => {
  const { selectedRotation, lang, canEdit, analyticsTriggers } = useRotationsContext();
  const addMembers = useAddMembersToTeam();
  const removeMember = useRemoveMemberFromTeam();

  const mapContactsToOptions = contact => ({
    label: formatFullName(contact),
    value: contact.id,
  });

  const filterExistingMembers = (id, group) => !group?.some(member => member.id === id);

  const staffOptions = contacts
    .filter(({ isUser = false }) => isUser)
    .filter(({ id }) => filterExistingMembers(id, selectedRotation?.team?.staff))
    .map(mapContactsToOptions);

  const contactOptions = contacts
    .filter(({ id }) => filterExistingMembers(id, selectedRotation?.team?.contacts))
    .map(mapContactsToOptions);

  const handleAdd = async (members, group) => {
    try {
      await addMembers.mutateAsync({
        rotationId: selectedRotation.id,
        group,
        members,
      });
      analyticsTriggers?.onAddTeamMember({ id: selectedRotation.id, group });
    } catch (e) {
      // Caller (TeamManager) shows its own UI feedback; nothing to do here.
    }
  };

  const handleRemove = async (memberId, group) => {
    try {
      await removeMember.mutateAsync({
        rotationId: selectedRotation.id,
        group,
        memberId,
      });
      analyticsTriggers?.onRemoveTeamMember({ id: selectedRotation.id, memberId, group });
    } catch (e) {
      // see above
    }
  };

  const renderTeamManager = ({
    group,
    availableOptions,
    notFoundText,
    canEdit,
    rootClassName = '',
    addUsersLabel,
  }) => (
    <TeamManager
      existingUsers={selectedRotation.team?.[group]?.map(({ id }) => getContactById(id)) || []}
      availableUsers={availableOptions}
      addUsersLabel={addUsersLabel}
      onAddUsers={users =>
        handleAdd(
          users.map(id => ({
            id,
            type: getContactById(id)?.isUser ? CONTACT_TYPES.USER : CONTACT_TYPES.CONTACT,
          })),
          group,
        )
      }
      onRemoveUser={id => handleRemove(id, group)}
      canEdit={canEdit}
      notFoundText={notFoundText}
      rootClassName={rootClassName}
    />
  );

  return (
    <div>
      <SectionComponent
        title={lang.CORE_STAFF.replace('{size}', selectedRotation?.team?.staff?.length || 0)}
        defaultIsOpen={selectedRotation?.team?.staff?.length > 0}
        showActions={false}>
        {renderTeamManager({
          group: TEAM_GROUPS.STAFF,
          availableOptions: staffOptions,
          notFoundText: lang.NOT_FOUND_STAFF,
          canEdit,
          rootClassName: styles.teamsWrapper,
          addUsersLabel: lang.ADD_CORE_STAFF,
        })}
      </SectionComponent>

      <SectionComponent
        title={lang.IMPORTANT_CONTACTS.replace(
          '{size}',
          selectedRotation?.team?.contacts?.length || 0,
        )}
        defaultIsOpen={selectedRotation?.team?.contacts?.length > 0}
        showActions={false}>
        {renderTeamManager({
          group: TEAM_GROUPS.CONTACTS,
          availableOptions: contactOptions,
          notFoundText: lang.NOT_FOUND_CONTACTS,
          canEdit,
          rootClassName: styles.teamsWrapper,
          addUsersLabel: lang.ADD_IMPORTANT_CONTACTS,
        })}
      </SectionComponent>

      <SectionComponent
        title={lang.TRAINEES.replace('{size}', selectedRotation?.team?.trainees?.length || 0)}
        defaultIsOpen={selectedRotation?.team?.trainees?.length > 0}
        showActions={false}>
        {renderTeamManager({
          group: TEAM_GROUPS.TRAINEES,
          availableOptions: [],
          notFoundText: lang.NOT_FOUND_TRAINEES,
          canEdit: false,
          rootClassName: styles.traineesTeamManager,
        })}
      </SectionComponent>
    </div>
  );
};

export { TeamTabComponent };
